import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

/**
 * Creates a clean multi-page PDF for item analytics using jsPDF-AutoTable.
 * No html2canvas: true vector text, selectable/searchable, and better pagination.
 */
export async function ItemAnalyticsPrint(
  items,
  { leadTimeDays = 60, title = "Item Sales & Restock Analytics" } = {}
) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const safe = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
  const num = (v, digits = 0) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "-";
    return n.toLocaleString(undefined, {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    });
  };

  const recentMonthsText = (months) => {
    const arr = Array.isArray(months) ? months : [];
    const last = arr.slice(-6);
    if (last.length === 0) return "-";
    return last.map((m) => `${m.month}:${m.soldQty}`).join("  |  ");
  };

  const rows = (Array.isArray(items) ? items : []).flatMap((it) => {
    const mainRow = [
      safe(it?.itemName),
      num(it?.currentStock),
      num(it?.sales?.avgDailySold, 4),
      num(it?.sales?.avgMonthlySold, 2),
      safe(it?.restock?.estimatedStockoutDate),
      safe(it?.restock?.reorderDate),
      num(it?.restock?.suggestedReorderQty),
    ];

    // second “details” row under each item (spans all columns)
    const detailRow = [
      {
        content: `Recent months: ${safe(recentMonthsText(it?.sales?.months))}`,
        colSpan: 7,
        styles: {
          fontSize: 9.5,
          textColor: [55, 65, 81], // gray-700
          fillColor: [255, 255, 255],
        },
      },
    ];

    return [mainRow, detailRow];
  });

  const nowText = dayjs().format("YYYY-MM-DD HH:mm");

  autoTable(pdf, {
    head: [["Item", "Stock", "Avg/Day", "Avg/Month", "Stockout", "Reorder", "Suggest Qty"]],
    body: rows,

    // Page + layout
    theme: "grid",
    margin: { top: 28, left: 5, right: 5, bottom: 16 },

    // Column widths + alignment
    columnStyles: {
      0: { cellWidth: 52, halign: "left" },  // Item
      1: { cellWidth: 18, halign: "right" }, // Stock
      2: { cellWidth: 20, halign: "right" }, // Avg/Day
      3: { cellWidth: 22, halign: "right" }, // Avg/Month
      4: { cellWidth: 28, halign: "center" },// Stockout
      5: { cellWidth: 28, halign: "center" },// Reorder
      6: { cellWidth: 26, halign: "right" }, // Suggest Qty
    },

    headStyles: {
      fillColor: [249, 250, 251], // gray-50
      textColor: [17, 24, 39],    // gray-900
      lineColor: [229, 231, 235], // gray-200
      lineWidth: 0.2,
      fontStyle: "bold",
    },

    bodyStyles: {
      textColor: [17, 24, 39],
      lineColor: [243, 244, 246], // gray-100
      lineWidth: 0.15,
      cellPadding: { top: 2.8, right: 2.2, bottom: 2.8, left: 2.2 },
      fontSize: 10.5,
      valign: "middle",
    },

    // Make "item main rows" slightly stronger, and suggested qty blue/bold
    didParseCell: (data) => {
      if (data.section !== "body") return;

      const isDetailRow = Array.isArray(data.row?.raw) === false; // colSpan row uses object cell
      if (!isDetailRow) {
        // main rows: first cell (item name) bold
        if (data.column.index === 0) data.cell.styles.fontStyle = "bold";

        // main rows: suggested qty emphasized
        if (data.column.index === 6) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [29, 78, 216]; // blue-700
        }
      } else {
        // detail row: remove grid feel a bit
        data.cell.styles.lineColor = [229, 231, 235]; // gray-200
        data.cell.styles.lineWidth = 0.2;
      }
    },

    // Header + footer (repeated on every page)
    didDrawPage: (data) => {
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Title block
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.text(title, data.settings.margin.left, 12);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(107, 114, 128); // gray-500
      pdf.text(
        `Lead Time: ${leadTimeDays} days`,
        data.settings.margin.left,
        18
      );

      // Right side meta
      pdf.text("Generated", pageWidth - data.settings.margin.right, 12, { align: "right" });
      pdf.setTextColor(17, 24, 39);
      pdf.setFont("helvetica", "bold");
      pdf.text(nowText, pageWidth - data.settings.margin.right, 17, { align: "right" });

      // Divider
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.4);
      pdf.line(data.settings.margin.left, 22, pageWidth - data.settings.margin.right, 22);

      // Footer note + page number
      const pageNumber = pdf.getCurrentPageInfo().pageNumber;
      const pageCount = pdf.getNumberOfPages();

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(156, 163, 175); // gray-400

      pdf.text(
        "This report is for planning purposes. Verify reorder quantities with supplier lead times and pending shipments.",
        pageWidth / 2,
        pageHeight - 8,
        { align: "center" }
      );

      pdf.text(
        `Page ${pageNumber} / ${pageCount}`,
        pageWidth - data.settings.margin.right,
        pageHeight - 8,
        { align: "right" }
      );

      // reset color for table rendering
      pdf.setTextColor(17, 24, 39);
    },
  });

  pdf.save("ITEM-ANALYTICS.pdf");
}